import type { FC, ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { AriaLiveMessage } from '@xing-com/crate-jobs-components-aria-live-message';
import { mediaConfined, zIndexStickyLayer3 } from '@xing-com/layout-tokens';
import {
  spaceXS,
  spaceXL,
  spaceXXL,
  space4XL,
  spaceS,
  xdlColorBackground,
} from '@xing-com/tokens';

import { WizardActions } from './components/wizard-actions';
import { WizardProgressBar } from './components/wizard-progress-bar';
import { WizardReview } from './components/wizard-review';
import type { Step, ExternalDocsLegacySupportProp } from './types';

const WizardContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  gap: ${spaceXL};

  @media ${mediaConfined} {
    gap: ${spaceXXL};
  }
`;

const HeaderContainer = styled.div`
  padding: ${space4XL} ${spaceXS} ${spaceS};
  position: sticky;
  top: 0px;
  background: ${xdlColorBackground};
  z-index: ${zIndexStickyLayer3}; // let the step content scroll below but keep the modal X button above

  &:focus {
    outline: none;
  }
`;

const StepContainer = styled.div`
  flex-grow: 1;
  padding: 0 ${spaceXS};

  @media ${mediaConfined} {
    flex-grow: 0;
  }
`;

type Props = {
  header?: ReactElement;
  steps: Step[];
  reviewStepContent?: ReactElement;
  onSubmit: () => void;
  trackingProps: {
    itemUrn: string;
    flags: string;
    page: string;
    context: string;
  };
  externalDocuments?: ExternalDocsLegacySupportProp;
  validate?: (stepIndex: number) => Promise<boolean>;
  isLoading?: boolean;
};

export const Wizard: FC<Props> = ({
  header,
  steps,
  reviewStepContent,
  onSubmit,
  validate,
  isLoading = false,
  externalDocuments,
  trackingProps,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  const totalSteps = steps.length + 1; // +1 to account for the review step
  const hasNext = currentStep < totalSteps;
  const hasPrevious = currentStep > 1;
  const isReviewStep = currentStep === totalSteps;

  useEffect(() => {
    if (isReviewStep) setIsReviewMode(isReviewStep);
  }, [isReviewStep]);

  useEffect(() => {
    // move the focus from next/back button to the first item on the view on step change
    headerRef.current?.focus();
  }, [currentStep]);

  const goToNextStep = async (): Promise<void> => {
    const isValid = await validate?.(currentStep);
    if (hasNext && isValid) setCurrentStep((step) => step + 1);
  };

  const goToPreviousStep = (): void => {
    if (hasPrevious) setCurrentStep((step) => step - 1);
  };

  const goToReviewStep = async (): Promise<void> => {
    const isValid = await validate?.(currentStep);
    if (isValid) setCurrentStep(totalSteps);
  };

  if (isReviewStep) {
    return (
      <WizardReview
        steps={steps}
        reviewStepContent={reviewStepContent}
        isLoading={isLoading}
        onReviewStep={(step) => setCurrentStep(step)}
        onSubmit={onSubmit}
        externalDocuments={externalDocuments}
        trackingProps={trackingProps}
      />
    );
  }

  return (
    <WizardContainer>
      <AriaLiveMessage messageId={steps[currentStep - 1].name} />
      <HeaderContainer tabIndex={-1} ref={headerRef} role="group">
        {currentStep === 1 ? header : null}
        <WizardProgressBar
          currentStep={currentStep}
          totalSteps={steps.length}
          isReviewMode={isReviewMode}
        />
      </HeaderContainer>
      <StepContainer>{steps[currentStep - 1].content}</StepContainer>
      <WizardActions
        hasPrevious={hasPrevious}
        isReviewMode={isReviewMode}
        isLoading={isLoading}
        onNext={goToNextStep}
        onBack={goToPreviousStep}
        onReview={goToReviewStep}
      />
    </WizardContainer>
  );
};
